import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
	IconButton,
	Pagination,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChatIcon from '@mui/icons-material/Chat';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SearchIcon from '@mui/icons-material/Search';
import fileDownload from 'js-file-download';
import DeleteIcon from '@mui/icons-material/Delete';
import { colors, theme } from '../../theme';
import { CustomButton } from '../shared/button/button';
import { CustomDate } from '../shared/date/date';
import { CustomDialog } from '../shared/dialog/dialog';
import { EditPanel } from './editPanel';
import { MultiSelectDropDown } from '../shared/dropdown/multiSelect';
import { Search } from '../shared/search/search';
import { ToolTip } from '../shared/tooltip/tooltip';
import { GetStatus, HasSkippablePosition, SkipValues, SkipValuesPreview } from '../../utils/routine';
import {
	DeactivateRoutineRequest,
	MatRoutineType,
	MaterialRoutineMaster,
	MaterialRoutineType,
	MaterialRoutineExportFilter,
	RoutineDetailsDataType,
	RoutineType,
	SaveMaterialRoutineRequest,
} from '../../types/routine.types';
import { ListType, PostResponse } from '../../types/common.types';
import { ClaimsThreshold, LogoClaimMaterials, Status } from '../../config/routinesConfig';
import { Status as MaterialStatus, InScope } from '../../config/cuTabConfig';
import { useAsyncOperation } from '../../utils/use-async-operation';
import {
	stopRoutine,
	getMaterialRoutines,
	getMatRoutineMaster,
	saveMaterialRoutine,
	exportData,
} from '../../services/material-routine-service';
import { Loader } from '../shared/loader/loader';
import { CustomAlert } from '../shared/alert/alert';
import { PlatformIcon, PlatformMaterials, Platforms } from 'src/config/constantsConfig';
import { RoutineDetailsDialog } from './routineDetails';
import DetailsIcon from '../../images/details.png';

export const testid = 'material-routine';

const color = colors();
const themes = theme().typography;

export const MaterialRoutine = (): ReactElement => {
	const classes = useStyles();
	const isAdmin: boolean = localStorage.getItem('roles')?.includes('TT.ReAdmin') || false;
	const BLANKS: string = '(Blanks)';
	const isSupplier: boolean = localStorage.getItem('isSupplierAccount') === 'true' || false;

	const [verRoutineList, setVerRoutineList] = useState<RoutineType[]>([]);
	const [docList, setDocList] = useState<ListType[]>([]);
	const [matTypeList, setMatTypeList] = useState<ListType[]>([]);
	const [matRoutines, setMatRoutines] = useState<MaterialRoutineType[]>([]);
	const [materialType, setMaterialType] = useState<string[]>([]);
	const [platform, setPlatform] = useState<string[]>([]);
	const [appliedRoutine, setAppliedRoutine] = useState<string[]>([]);
	const [requiredDoc, setRequiredDoc] = useState<string[]>([]);
	const [status, setStatus] = useState<string[]>(isSupplier ? ['Active'] : []);
	const [matStatus, setMatStatus] = useState<string[]>(isSupplier ? ['Active'] : []);
	const [sustainableClaimFilter, setSustainableClaimFilter] = useState<string[]>([]);
	const [logoClaim, setLogoClaim] = useState<string[]>([]);
	const [searchText, setSearchText] = useState<string>();
	const [matRoutine, setMatRoutine] = useState<MatRoutineType>();
	const [deactiveDate, setDeactiveDate] = useState<string>();
	const [deactivateOpen, setDeactivateOpen] = useState<boolean>(false);
	const [disableActions, setDisableActions] = useState<boolean>(false);
	const [openError, setOpenError] = useState(false);
	const [openSaveAlert, setOpenSaveAlert] = useState(false);
	const [showBlanks, setShowBlanks] = useState(false);
	const [exportBusy, setExportBusy] = useState(false);
	const [exportError, setExportError] = useState(false);
	const [openDetailsDialog, setOpenDetailsDialog] = useState<boolean>(false);
	const [materialId, setMaterialId] = useState<string>('');
	const initialMasterLoadTriggered = useRef<boolean>(false);
	const initialRoutineLoadTriggered = useRef<boolean>(false);
	const [canShowClearButton, setCanShowClearButton] = useState<boolean>(false);
	const [clearFilters, setClearFilters] = useState<boolean>(false);
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState<number>(25);
	const [pageCount, setPageCount] = useState<number>(1);

	const [getMatRoutines, matRoutineBusy, materialRoutines, matRoutineError] =
		useAsyncOperation<MaterialRoutineType[]>(getMaterialRoutines);
	const [getMasterData, masterBusy, masterData, masterError] =
		useAsyncOperation<MaterialRoutineMaster>(getMatRoutineMaster);
	const [saveMatRoutine, saveBusy, saveData, saveError] = useAsyncOperation<PostResponse>(saveMaterialRoutine);
	const [deactivateRoutine, deactivateBusy, deactivateData, deactivateError] =
		useAsyncOperation<PostResponse>(stopRoutine);

	useEffect(() => {
		if (!initialMasterLoadTriggered.current) {
			initialMasterLoadTriggered.current = true;
			getMasterData();
		}
		if (!initialRoutineLoadTriggered.current) {
			initialRoutineLoadTriggered.current = true;
			getMatRoutines();
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (matRoutineError || masterError) setOpenError(true);
	}, [matRoutineError, masterError]);

	useEffect(() => {
		if (masterData) {
			setVerRoutineList(masterData.routines);
			setDocList(masterData.documentMaster);
			setMatTypeList(masterData.materialTypes);
		}
	}, [masterData]);

	useEffect(() => {
		if (materialRoutines) {
			setMatRoutines(applyFilters(materialRoutines));
		}
	}, [materialRoutines]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (clearFilters) {
			setMatRoutines(applyFilters(materialRoutines || []));
			setClearFilters(false);
		}
	}, [clearFilters]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (deactivateData) {
			getMatRoutines();
		}
	}, [deactivateData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (saveData) {
			setOpenDetailsDialog(false);
			getMatRoutines();
		}
	}, [saveData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (deactivateData || saveData || deactivateError || saveError) setOpenSaveAlert(true);
	}, [deactivateData, saveData, deactivateError, saveError]);

	useEffect(() => {
		setPageCount(Math.floor(matRoutines.length / rowsPerPage));
	}, [rowsPerPage, matRoutines]);

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
	};

	const handleRowPerPageChange = (value: string) => {
		setRowsPerPage(parseInt(value));
		setPage(1);
	};

	const onClearClick = () => {
		setPage(1);
		if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
			try {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			} catch {
				window.scrollTo(0, 0);
			}
		}
		setMaterialType([]);
		setPlatform([]);
		setAppliedRoutine([]);
		setRequiredDoc([]);
		setStatus(isSupplier ? ['Active'] : []);
		setMatStatus(isSupplier ? ['Active'] : []);
		setSustainableClaimFilter([]);
		setLogoClaim([]);
		setSearchText(undefined);
		setShowBlanks(false);
		setClearFilters(true);
		setCanShowClearButton(false);
	};

	const applyFilters = (sourceData: MaterialRoutineType[]) => {
		let data = sourceData || [];
		const useBlanks = showBlanks || appliedRoutine.includes(BLANKS);

		if (materialType.length > 0) {
			data = data.filter((d) => materialType.includes(d.materialType));
		}
		if (platform.length > 0 && data.length > 0) {
			data = data.filter((d) =>
				PlatformMaterials.filter((p) => platform.includes(p.platform))
					.map((p) => p.materialId)
					.includes(d.materialCompositionTypeId)
			);
		}
		if (appliedRoutine.length > 0 && data.length > 0) {
			data = useBlanks
				? data.filter((d) => d.routines.length === 0)
				: data
						.map((d) => {
							return { ...d, routines: d.routines.filter((r) => appliedRoutine.includes(r.routineName)) };
						})
						.filter((d) => d.routines.length > 0);
		}
		if (requiredDoc.length > 0 && data.length > 0) {
			data = data
				.map((d) => {
					return { ...d, routines: d.routines.filter((r) => requiredDoc.includes(r.documentName)) };
				})
				.filter((d) => d.routines.length > 0);
		}
		if (status.length > 0 && data.length > 0) {
			data = data
				.map((d) => {
					return {
						...d,
						routines: d.routines.filter((r) => status.includes(GetStatus(r.activeDate, r.endDate).statusName)),
					};
				})
				.filter((d) => d.routines.length > 0);
		}
		if (matStatus.length === 1 && data.length > 0) {
			data = data.filter((d) => (matStatus[0] === 'Active' ? d.isActive : !d.isActive));
		}
		if (sustainableClaimFilter.length === 1 && data.length > 0) {
			data = data.filter((d) =>
				sustainableClaimFilter[0] === 'Yes'
					? d.sustainableClaim?.toLowerCase() === 'yes'
					: d.sustainableClaim?.toLowerCase() !== 'yes'
			);
		}
		if (logoClaim.length === 1 && data.length > 0) {
			data = data.filter((d) =>
				logoClaim[0] === 'Yes'
					? LogoClaimMaterials.includes(d.materialCompositionTypeId)
					: !LogoClaimMaterials.includes(d.materialCompositionTypeId)
			);
		}
		if (searchText && data.length > 0) {
			data = data.filter(
				(d) =>
					d.materialCompositionTypeName.toLowerCase().includes(searchText.toLowerCase()) ||
					d.materialCompositionTypeId.includes(searchText)
			);
		}
		if (useBlanks && (appliedRoutine.length === 0 || data.length > 0)) {
			data = data.filter((d) => d.routines.length === 0);
		}

		setShowBlanks(useBlanks);
		return data;
	};

	const renderPlatformIcons = (materialCompositionTypeId: string) => {
		const platformNames = [
			...new Set(PlatformMaterials.filter((e) => e.materialId === materialCompositionTypeId).map((e) => e.platform)),
		];

		return platformNames
			.map((platformName) => {
				const iconElement = PlatformIcon.find((p) => p.platform === platformName)?.image;
				return iconElement
					? React.cloneElement(iconElement, {
							key: `${materialCompositionTypeId}-${platformName}`,
					  })
					: null;
			})
			.filter((icon): icon is ReactElement => icon !== null);
	};

	const onSearchClick = () => {
		setPage(1);
		setMatRoutines(applyFilters(materialRoutines || []));
		setCanShowClearButton(true);
	};

	const handleDeactiveClick = (row: MatRoutineType) => {
		setMatRoutine(row);
		setDeactivateOpen(true);
	};

	const handleAddClick = (matId: string) => {
		let data = [...matRoutines];
		data
			.filter((d) => d.materialCompositionTypeId === matId)[0]
			.routines.push({ ruleId: '-1', isModify: true } as MatRoutineType);
		setMatRoutines(data);
		setDisableActions(true);
	};

	const handleEditClick = (matId: string, row: MatRoutineType) => {
		let data = [...matRoutines];
		data
			.filter((d) => d.materialCompositionTypeId === matId)[0]
			.routines.filter((r) => r.ruleId === row.ruleId)[0].isModify = true;
		data
			.filter((d) => d.materialCompositionTypeId === matId)[0]
			.routines.filter((r) => r.ruleId === row.ruleId)[0].isEdit = true;
		setMatRoutines(data);
		setDisableActions(true);
	};

	const handleDetailsClick = (matId: string, row: MatRoutineType) => {
		setMatRoutine(row);
		setMaterialId(matId);
		setOpenDetailsDialog(true);
	};

	const onDeactivateSaveClick = () => {
		let deactivateData: DeactivateRoutineRequest = {
			ruleId: matRoutine?.ruleId || '',
			endDate: deactiveDate || '',
		};

		deactivateRoutine(deactivateData);
		setDeactivateOpen(false);
		setMatRoutine(undefined);
		setDeactiveDate(undefined);
	};

	const clearFlag = (matId: string) => {
		let data = [...matRoutines];
		if (data.filter((d) => d.materialCompositionTypeId === matId)[0].routines.filter((r) => r.isEdit).length > 0) {
			data
				.filter((d) => d.materialCompositionTypeId === matId)[0]
				// eslint-disable-next-line array-callback-return
				.routines.map((r) => {
					r.isModify = false;
					r.isEdit = false;
				});
		} else {
			let index = data.filter((d) => d.materialCompositionTypeId === matId)[0].routines.length;
			data.filter((d) => d.materialCompositionTypeId === matId)[0].routines.splice(index - 1, 1);
		}

		setMatRoutines(data);
		setDisableActions(false);
	};

	const onSaveClickCallback = (matId: string, data: MatRoutineType) => {
		clearFlag(matId);
		let routine: SaveMaterialRoutineRequest = {
			materialCompositionTypeId: matId,
			materialCompositionTypeName: matRoutines.filter((m) => m.materialCompositionTypeId === matId)[0]
				.materialCompositionTypeName,
			materialType: matRoutines.filter((m) => m.materialCompositionTypeId === matId)[0].materialType,
			routine: data,
		};

		saveMatRoutine(routine);
	};

	const handleRoutineUpdate = (updatedRoutine: RoutineDetailsDataType) => {
		if (matRoutine) {
			let saveData: MatRoutineType = {
				...matRoutine,
				materialDescription: updatedRoutine.materialDescription,
				compositionRequirement: updatedRoutine.compositionRequirement,
				blendingRestriction: updatedRoutine.blendingRestriction,
				sampleCertification: updatedRoutine.sampleCertification,
				certificationApplication: updatedRoutine.certificationApplication,
				materialDocuments:
					updatedRoutine.uploadedDocuments?.filter((doc) => doc.documentType.toLowerCase() === 'material') || [],
			};
			let routine: SaveMaterialRoutineRequest = {
				materialCompositionTypeId: materialId,
				materialCompositionTypeName: matRoutines.filter((m) => m.materialCompositionTypeId === materialId)[0]
					.materialCompositionTypeName,
				materialType: matRoutines.filter((m) => m.materialCompositionTypeId === materialId)[0].materialType,
				routine: saveData,
			};

			saveMatRoutine(routine);
		}
	};

	return (
		<>
			<div className={classes.topPadding}>
				<Loader
					key={`${testid}-loader`}
					open={matRoutineBusy || masterBusy || saveBusy || deactivateBusy || exportBusy}
					message={exportBusy ? 'Exporting data...' : matRoutineBusy || masterBusy ? 'Loading...' : 'Updating...'}
				/>
				<CustomAlert
					open={openError}
					message="Something went wrong while retrieving data!"
					severity="error"
					timeout={5000}
					isFade={false}
					onCloseCallback={() => {
						setOpenError(false);
					}}
				/>
				<MultiSelectDropDown
					label="Material Type"
					width={105}
					data={matTypeList.map((list) => list.name)}
					value={materialType}
					onSelectCallback={setMaterialType}
				/>
				<MultiSelectDropDown
					label="Material Platform"
					width={123}
					data={Platforms}
					value={platform}
					onSelectCallback={setPlatform}
				/>
				<MultiSelectDropDown
					label="Applied Routine"
					width={117}
					data={[...verRoutineList.map((list) => list.routineName), BLANKS]}
					value={appliedRoutine}
					onSelectCallback={setAppliedRoutine}
				/>
				{!showBlanks && (
					<MultiSelectDropDown
						label="Required document"
						width={137}
						data={docList.map((list) => list.name)}
						value={requiredDoc}
						onSelectCallback={setRequiredDoc}
					/>
				)}
				{!isSupplier && !showBlanks && (
					<MultiSelectDropDown
						label="Rule Status"
						width={94}
						data={Status && Status.map((list) => list.name)}
						value={status}
						onSelectCallback={setStatus}
					/>
				)}

				{!isSupplier && (
					<MultiSelectDropDown
						label="Material Status"
						width={113}
						data={MaterialStatus.map((list) => list.value?.toString() || '')}
						value={matStatus}
						onSelectCallback={setMatStatus}
					/>
				)}

				{!isSupplier && <MultiSelectDropDown
					label="Sustainable Claim"
					width={128}
					data={InScope.map((list) => list.value?.toString() || '')}
					value={sustainableClaimFilter}
					onSelectCallback={setSustainableClaimFilter}
				/>}
				
				{!isSupplier && (
					<MultiSelectDropDown
						label="Certification Logo Claims"
						width={165}
						data={InScope.map((list) => list.value?.toString() || '')}
						value={logoClaim}
						onSelectCallback={setLogoClaim}
					/>
				)}
				<Search
					placeholder="Material Name, ID..."
					value={searchText || ''}
					onSearchCallback={setSearchText}
					onEnterSearch={onSearchClick}
				/>

				<CustomButton
					id="search"
					text=""
					startIcon={<SearchIcon />}
					width={50}
					variant="outlined"
					onClickCallback={onSearchClick}
				/>

				{(materialType.length > 0 ||
					platform.length > 0 ||
					appliedRoutine.length > 0 ||
					requiredDoc.length > 0 ||
					status.length > 0 ||
					matStatus.length > 0 ||
					sustainableClaimFilter.length > 0 ||
					logoClaim.length > 0 ||
					searchText !== undefined) &&
					canShowClearButton && (
						<CustomButton id="clear" text="" startIcon={<DeleteIcon />} width={50} onClickCallback={onClearClick} />
					)}
			</div>
			{!isSupplier && (<ToolTip
				tooltipText="Export to Excel"
				children={
					<img
						alt="export"
						src={require('../../images/excel.png')}
						className={classes.export}
						onClick={() => {
							setExportBusy(true);
							const filters: MaterialRoutineExportFilter = {
								materialType,
								platform,
								appliedRoutine,
								requiredDoc,
								status,
								matStatus,
								sustainableClaimFilter,
								logoClaim,
								searchText,
								showBlanks,
							};

							exportData(filters)
								.then((res) => {
									const blob = new Blob([res.data]);
									fileDownload(blob, 'Material Rules.xlsx');
									setExportBusy(false);
								})
								.catch(() => {
									setExportError(true);
									setExportBusy(false);
								});
						}}
					/>
				}
			/>)}
			<TableContainer className={classes.table} data-testid={`${testid}-container`}>
				<Table stickyHeader>
					<TableHead>
						<TableRow key={`${testid}-header`}>
							<TableCell width="5%">ID</TableCell>
							<TableCell width="15%">Material Name</TableCell>
							<TableCell width="31%">Required Document</TableCell>
							{!isSupplier && <TableCell width="10%">Skippable</TableCell>}
							<TableCell width="10%">Rule Status</TableCell>
							{!isSupplier && <TableCell width="10%">Sustainable Claim</TableCell>}
							{!isSupplier && <TableCell width="13%">Certification Logo Claims</TableCell>}
							{isAdmin && <TableCell width="1%" />}
						</TableRow>
					</TableHead>
					<TableBody data-testid={`${testid}-body`}>
						{(rowsPerPage > 0
							? matRoutines.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage)
							: matRoutines
						).map((mat) =>
							mat.routines === null || mat.routines.length === 0 ? (
								<TableRow
									hover
									key={`${testid}-${mat.materialCompositionTypeId}-0`}
									data-testid={`${testid}-${mat.materialCompositionTypeId}-0`}
								>
									<TableCell className={!mat.isActive ? classes.inactive : ''}>
										{mat.materialCompositionTypeId}
									</TableCell>
									<TableCell>
										<ToolTip
											tooltipText={mat.isActive ? '' : 'This Material is inactive'}
											children={
												<span>
													<div className={`${!mat.isActive ? classes.inactive : ''} ${classes.matName}`}>
														{mat.materialCompositionTypeName}
														{renderPlatformIcons(mat.materialCompositionTypeId)}
													</div>
													<div className={!mat.isActive ? classes.secondaryTextInactive : classes.secondaryText}>
														{mat.materialType}
													</div>
												</span>
											}
										/>
									</TableCell>
									<TableCell>--</TableCell>
									<TableCell>--</TableCell>
									<TableCell>--</TableCell>
									<TableCell>
										<div className={`${classes.skip} ${!mat.isActive ? classes.inactive : ''}`}>
											{mat.sustainableClaim}
											<ToolTip
												tooltipText={
													mat.isActive &&
													ClaimsThreshold.find((claim) => claim.materialId === mat.materialCompositionTypeId)
														? `Claims Threshold: ${
																ClaimsThreshold.find((claim) => claim.materialId === mat.materialCompositionTypeId)
																	?.threshold
														  }%`
														: ''
												}
												children={
													<span>
														{mat.isActive &&
															ClaimsThreshold.find((claim) => claim.materialId === mat.materialCompositionTypeId) && (
																<InfoOutlinedIcon id="skippct" />
															)}
													</span>
												}
											/>
										</div>
									</TableCell>
									<TableCell>
										<div className={!mat.isActive ? classes.inactive : ''}>
											{LogoClaimMaterials.includes(mat.materialCompositionTypeId) ? 'Yes' : 'No'}
										</div>
									</TableCell>
									{isAdmin && (
										<TableCell align="right">
											{mat.isActive && (
												<ToolTip
													tooltipText="Add routine"
													children={
														<span>
															<IconButton
																id="add-routine"
																className={classes.iconButton}
																disabled={disableActions}
																onClick={() => handleAddClick(mat.materialCompositionTypeId)}
															>
																<AddIcon sx={{ color: disableActions ? color.neutral[5] : color.primary[90] }} />
															</IconButton>
														</span>
													}
												/>
											)}
										</TableCell>
									)}
								</TableRow>
							) : (
								<React.Fragment key={`${testid}-${mat.materialCompositionTypeId}-rows`}>
									{mat.routines.map((row, i) => (
										<TableRow
											hover={row.isModify ? false : true}
											key={`${testid}-${mat.materialCompositionTypeId}-${row.ruleId}`}
											data-testid={`${testid}-${mat.materialCompositionTypeId}-${row.ruleId}`}
										>
											{row.isModify ? (
												<EditPanel
													materialId={mat.materialCompositionTypeId}
													materialName={mat.materialCompositionTypeName}
													materialType={mat.materialType}
													row={row}
													verRoutinesList={verRoutineList}
													onDiscardClick={clearFlag}
													onSaveClick={onSaveClickCallback}
												/>
											) : (
												<>
													<TableCell
														className={`${!mat.isActive ? classes.inactive : ''} ${
															i < mat.routines.length - 1 ? classes.noBorder : ''
														}`}
													>
														{i === 0 && mat.materialCompositionTypeId}
													</TableCell>
													<TableCell className={i < mat.routines.length - 1 ? classes.noBorder : ''}>
														<ToolTip
															tooltipText={mat.isActive ? '' : 'This Material is inactive'}
															children={
																<span>
																	<div className={`${!mat.isActive ? classes.inactive : ''} ${classes.matName}`}>
																		<span>
																			{i === 0 && mat.materialCompositionTypeName}
																			{i === 0 && renderPlatformIcons(mat.materialCompositionTypeId)}
																		</span>
																	</div>
																	<div
																		className={!mat.isActive ? classes.secondaryTextInactive : classes.secondaryText}
																	>
																		{i === 0 && mat.materialType}
																	</div>
																</span>
															}
														/>
													</TableCell>
													<TableCell>
														<div className={`${classes.skip} ${!mat.isActive ? classes.inactive : ''}`}>
															{row.documentName}
															{row.comments && (
																<ToolTip
																	tooltipText={row.comments}
																	children={
																		<span>
																			<ChatIcon id="comment" />
																		</span>
																	}
																/>
															)}
															{row.routineName !== 'NA' && (
																<ToolTip
																	tooltipText={
																		isAdmin && mat.isActive ? 'Update Routine Details' : 'View Routine Details'
																	}
																	children={
																		<span>
																			<img
																				src={DetailsIcon}
																				className={classes.detailsButton}
																				alt={isAdmin ? 'Update Routine Details' : 'View Routine Details'}
																				onClick={() => handleDetailsClick(mat.materialCompositionTypeId, row)}
																			/>
																		</span>
																	}
																/>
															)}
														</div>
														<div className={!mat.isActive ? classes.secondaryTextInactive : classes.secondaryText}>
															{row.routineName}
														</div>
													</TableCell>
													{!isSupplier && (
														<TableCell>
															<div className={`${classes.skip} ${!mat.isActive ? classes.inactive : ''}`}>
																{row.routineName !== 'NA' &&
																	(HasSkippablePosition(row) || row.skippablePct > 0 ? 'Skippable' : 'Not Skippable')}
																{row.skippablePct > 0 && (
																	<ToolTip
																		tooltipText={`This document is skippable if the material composition is less than ${row.skippablePct}%`}
																		children={
																			<span>
																				<InfoOutlinedIcon id="skippct" />
																			</span>
																		}
																	/>
																)}
															</div>
															<div className={!mat.isActive ? classes.secondaryTextInactive : classes.secondaryText}>
																{row.routineName !== 'NA' && (
																	<span title={SkipValues(row, row.skippablePct > 0)}>
																		{SkipValuesPreview(row, row.skippablePct > 0, 2)}
																	</span>
																)}
															</div>
														</TableCell>
													)}
													<TableCell>
														<div
															className={classes.status}
															style={{
																background: `${
																	mat.isActive
																		? GetStatus(row.activeDate, row.endDate).backgroundColor
																		: color.neutral[3]
																}`,
																color: `${
																	mat.isActive ? GetStatus(row.activeDate, row.endDate).textColor : color.neutral[5]
																}`,
															}}
														>
															{GetStatus(row.activeDate, row.endDate).status}
														</div>
														{(row.activeDate || row.endDate) && (
															<div className={!mat.isActive ? classes.secondaryTextInactive : classes.secondaryText}>
																{GetStatus(row.activeDate, row.endDate).statusDuration}
															</div>
														)}
													</TableCell>
													{!isSupplier && <TableCell>
														<div className={`${classes.skip} ${!mat.isActive ? classes.inactive : ''}`}>
															{mat.sustainableClaim}
															<ToolTip
																tooltipText={
																	mat.isActive &&
																	ClaimsThreshold.find((claim) => claim.materialId === mat.materialCompositionTypeId)
																		? `Claims Threshold: ${
																				ClaimsThreshold.find(
																					(claim) => claim.materialId === mat.materialCompositionTypeId
																				)?.threshold
																		  }%`
																		: ''
																}
																children={
																	<span>
																		{mat.isActive &&
																			ClaimsThreshold.find(
																				(claim) => claim.materialId === mat.materialCompositionTypeId
																			) && <InfoOutlinedIcon id="skippct" />}
																	</span>
																}
															/>
														</div>
													</TableCell>}
													{!isSupplier && <TableCell>
														<div className={!mat.isActive ? classes.inactive : ''}>
															{LogoClaimMaterials.includes(mat.materialCompositionTypeId) ? 'Yes' : 'No'}
														</div>
													</TableCell>}
													{isAdmin && (
														<TableCell align="right">
															{mat.isActive && (
																<div className={classes.actions}>
																	{!row.endDate && (!row.activeDate || new Date() > new Date(row.activeDate)) && (
																		<ToolTip
																			tooltipText="Deactivate"
																			children={
																				<span>
																					<IconButton
																						id="deactivate"
																						className={classes.iconButton}
																						disabled={disableActions}
																						onClick={() => handleDeactiveClick(row)}
																					>
																						<PowerSettingsNewIcon
																							sx={{ color: disableActions ? color.neutral[5] : color.primary[90] }}
																						/>
																					</IconButton>
																				</span>
																			}
																		/>
																	)}
																	{!row.endDate && row.activeDate && new Date() < new Date(row.activeDate) && (
																		<ToolTip
																			tooltipText="Edit routine"
																			children={
																				<span>
																					<IconButton
																						id="edit-doc"
																						className={classes.iconButton}
																						disabled={disableActions}
																						onClick={() => handleEditClick(mat.materialCompositionTypeId, row)}
																					>
																						<EditOutlinedIcon
																							sx={{ color: disableActions ? color.neutral[5] : color.primary[90] }}
																						/>
																					</IconButton>
																				</span>
																			}
																		/>
																	)}
																	<ToolTip
																		tooltipText="Add routine"
																		children={
																			<span>
																				<IconButton
																					id="add-routine"
																					className={classes.iconButton}
																					disabled={disableActions}
																					onClick={() => handleAddClick(mat.materialCompositionTypeId)}
																				>
																					<AddIcon
																						fontSize="inherit"
																						sx={{ color: disableActions ? color.neutral[5] : color.primary[90] }}
																					/>
																				</IconButton>
																			</span>
																		}
																	/>
																</div>
															)}
														</TableCell>
													)}
												</>
											)}
										</TableRow>
									))}
								</React.Fragment>
							)
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				data-testid={`${testid}-pagination`}
				className={classes.pagination}
				rowsPerPageOptions={[25, 50, 100]}
				rowsPerPage={rowsPerPage}
				count={matRoutines.length}
				page={page - 1}
				onPageChange={() => {}}
				onRowsPerPageChange={(e) => handleRowPerPageChange(e.target.value)}
				ActionsComponent={() => (
					<Pagination
						data-testid={`${testid}-node`}
						count={matRoutines.length % rowsPerPage === 0 ? pageCount : pageCount + 1}
						page={page}
						onChange={handlePageChange}
						shape="rounded"
					/>
				)}
			/>
			<CustomDialog
				open={deactivateOpen}
				title="Deactivate routine"
				yesButtonText="Apply"
				noButtonText="Cancel"
				yesButtonDisable={!deactiveDate ? true : false}
				onYesClickCallback={onDeactivateSaveClick}
				onNoClickCallback={() => {
					setDeactivateOpen(false);
					setDeactiveDate(undefined);
				}}
				onCloseCallback={() => {
					setDeactivateOpen(false);
					setDeactiveDate(undefined);
				}}
				component={
					<>
						<CustomDate
							width={332}
							label=""
							labelText="Deactivate date"
							format="DD MMM, YYYY"
							onChangeCallback={setDeactiveDate}
							disablePast
						/>
						<div className={classes.skipDialogText}>
							Orders with an OPD after the specified deactivation date will not be subject to the routine
							<b> {matRoutine?.documentName}</b> (<b>{matRoutine?.routineName}</b>).
						</div>
					</>
				}
			/>
			{openSaveAlert && (
				<div className={classes.alert}>
					<CustomAlert
						open={openSaveAlert}
						timeout={5000}
						message={
							(deactivateData || saveData) && !deactivateError && !saveError
								? 'Rule saved successfully!'
								: 'Something went wrong!'
						}
						severity={(deactivateData || saveData) && !deactivateError && !saveError ? 'success' : 'error'}
						onCloseCallback={() => {
							setOpenSaveAlert(false);
						}}
					/>
				</div>
			)}
			{exportError && (
				<div className={classes.alert}>
					<CustomAlert
						open={exportError}
						timeout={5000}
						message="Unable to export data. Please try after sometime!"
						severity="error"
						onCloseCallback={() => setExportError(false)}
					/>
				</div>
			)}
			{openDetailsDialog && matRoutine && matRoutine.ruleId && (
				<RoutineDetailsDialog
					isAdmin={isAdmin && matRoutines.filter((m) => m.materialCompositionTypeId === materialId)[0].isActive}
					isMaterial={true}
					isError={!!saveError}
					data={
						{
							id: matRoutine.ruleId,
							isMaterial: true,
							materialName: matRoutines.filter((m) => m.materialCompositionTypeId === materialId)[0]
								.materialCompositionTypeName,
							materialDescription: matRoutine.materialDescription,
							routineName: matRoutine.routineName,
							routineDescription: matRoutine.routineDescription,
							compositionRequirement: matRoutine.compositionRequirement,
							blendingRestriction: matRoutine.blendingRestriction,
							sampleCertification: matRoutine.sampleCertification,
							certificationApplication: matRoutine.certificationApplication,
							uploadedDocuments: matRoutine.materialDocuments,
							allowMaterialEdits: matRoutine.allowMaterialEdits,
						} as RoutineDetailsDataType
					}
					onSaveClick={(data) => {
						handleRoutineUpdate(data);
					}}
					onCancelClick={() => setOpenDetailsDialog(false)}
				/>
			)}
		</>
	);
};

const useStyles = makeStyles(() => ({
	topPadding: {
		paddingTop: '15px',
		marginLeft: '-20px',
		'& #search': {
			backgroundColor: color.primary[100],
			color: color.text.light,
			minWidth: '40px !important',
			marginLeft: '5px',
			width: '40px',
			paddingRight: '5px',
		},
		'& #clear': {
			minWidth: '40px !important',
			width: '40px',
			marginLeft: '5px',
			paddingRight: '5px',
		},
	},
	table: {
		marginTop: '55px',
		height: 'calc(100% - 265px)',
		'& .MuiTableCell-head': {
			...themes.bodyMedium,
			color: color.neutral[8],
			backgroundColor: color.neutral[1],
			borderTop: `1px solid ${color.neutral[2]}`,
		},
		'& .MuiTableCell-body': {
			...themes.bodyRegular,
			color: color.text.dark,
			padding: '3px 8px !important',
		},
		'& .MuiTableCell-root': {
			borderBottom: `1px solid ${color.neutral[2]}`,
			padding: '13px 8px',
		},
		'& .MuiTableRow-hover': {
			'&:hover': {
				backgroundColor: `${color.signal.infoLight} !important`,
			},
		},
	},
	noBorder: {
		borderBottom: 'none !important',
	},
	secondaryText: {
		...themes.metaText,
		color: color.neutral[8],
	},
	secondaryTextInactive: {
		...themes.metaText,
		color: color.neutral[5],
	},
	skip: {
		display: 'flex',
		alignItems: 'center',
		'& #skippct, #comment': {
			display: 'flex',
			width: '16px',
			height: '16px',
			color: color.primary[90],
			marginLeft: '5px',
			cursor: 'pointer',
		},
	},
	status: {
		...themes.metaText,
		width: 'fit-content',
		padding: '0px 8px',
		borderRadius: '4px',
	},
	skipDialogText: {
		...themes.metaText,
		margin: '20px 8px',
	},
	iconButton: {
		padding: '5px !important',
		'& svg': {
			width: '16px',
			height: '16px',
		},
	},
	detailsButton: {
		width: '22px',
		height: '22px',
		verticalAlign: 'top',
		cursor: 'pointer',
	},
	actions: {
		display: 'flex',
		justifyContent: 'end',
	},
	pagination: {
		'& .MuiToolbar-root.MuiTablePagination-toolbar': {
			position: 'absolute',
			right: 20,
			paddingTop: '5px',
		},
		'& .MuiButtonBase-root.Mui-selected': {
			backgroundColor: color.primary[100],
			color: color.shades.white,
		},
		'& .MuiButtonBase-root.Mui-selected:hover': {
			backgroundColor: color.primary[100],
		},
		'& .MuiPagination-root.MuiPagination-text': {
			flexShrink: 0,
			marginLeft: '10px',
		},
	},
	alert: {
		position: 'fixed',
		top: '10px',
		left: '50%',
		transform: 'translateX(-50%)',
	},
	inactive: {
		color: `${color.neutral[5]} !important`,
	},
	export: {
		position: 'absolute',
		right: '15px',
		padding: '15px',
		cursor: 'pointer',
		fontSize: '30px !important',
		width: '22px',
		height: '22px',
		'&:hover': {
			background: '#0000000a',
			borderRadius: '50%',
		},
	},
	matName: {
		display: 'inline',
		'& a img': {
			verticalAlign: 'middle',
		},
	},
}));
